// frontend/src/utils/permissions.ts

export const ROLES = {
  ADMIN: 'Admin',
  MANAGER: 'Manager',
  STAFF: 'Staff',
} as const;

export type UserRole = (typeof ROLES)[keyof typeof ROLES];

// Permission matrix:
// | Feature                      | Admin |  Manager  | Staff |
// |------------------------------|-------|-----------|-------|
// | View clients/pets            |   Y   |     Y     |   N   |
// | Create/Edit clients/pets     |   Y   |     Y     |   N   |
// | Delete clients/pets/bookings |   Y   |     Y     |   N   |
// | View bookings                |   Y   |     Y     |   Y   |
// | Create/Edit bookings         |   Y   |     Y     |   N   |
// | Today's page                 |   Y   |     Y     |   Y   |
// | Today's page - Overdue tab   |   Y   |     Y     |   N   |
// | View kennels                 |   Y   |     Y     |   Y   |
// | Manage kennels/settings      |   Y   |     Y     |   N   |
// | Manage users                 |   Y   |     N     |   N   |
// | Error logs                   |   Y   |     N     |   N   |
// | Broadcast messages           |   Y   |     Y     |   N   |

export const Permissions = {
  canViewClients: (role: string | undefined): boolean => {
    return role === ROLES.ADMIN || role === ROLES.MANAGER;
  },

  canCreate: (role: string | undefined): boolean => {
    return role === ROLES.ADMIN || role === ROLES.MANAGER;
  },

  canEdit: (role: string | undefined): boolean => {
    return role === ROLES.ADMIN || role === ROLES.MANAGER;
  },

  canDelete: (role: string | undefined): boolean => {
    return role === ROLES.ADMIN || role === ROLES.MANAGER;
  },

  canViewBookings: (role: string | undefined): boolean => {
    return (
      role === ROLES.ADMIN || role === ROLES.MANAGER || role === ROLES.STAFF
    );
  },

  canEditBookings: (role: string | undefined): boolean => {
    return role === ROLES.ADMIN || role === ROLES.MANAGER;
  },

  canManageSettings: (role: string | undefined): boolean => {
    return role === ROLES.ADMIN || role === ROLES.MANAGER;
  },

  // Staff's role on Today's Page is operational (who's checking in/out
  // today), not financial - overdue invoices are invoicing/accounts
  // visibility, which Staff can't reach anywhere else in the app either
  // (InvoiceController is Admin/Manager-only end to end).
  canViewOverdueInvoices: (role: string | undefined): boolean => {
    return role === ROLES.ADMIN || role === ROLES.MANAGER;
  },

  canManageUsers: (role: string | undefined): boolean => {
    return role === ROLES.ADMIN;
  },

  canBroadcastMessages: (role: string | undefined): boolean => {
    return role === ROLES.ADMIN || role === ROLES.MANAGER;
  },

  isAdmin: (role: string | undefined): boolean => {
    return role === ROLES.ADMIN;
  },

  isManager: (role: string | undefined): boolean => {
    return role === ROLES.MANAGER;
  },

  isStaff: (role: string | undefined): boolean => {
    return role === ROLES.STAFF;
  },
};

export const getRoleDisplayName = (role: string | undefined): string => {
  if (!role) return 'Unknown';
  return role;
};

export const getAllRoles = (): UserRole[] => {
  return [ROLES.ADMIN, ROLES.MANAGER, ROLES.STAFF];
};

export const isValidRole = (role: string): role is UserRole => {
  return Object.values(ROLES).includes(role as UserRole);
};
